import axios from 'axios';
import { LoginUserModel } from '../types/LoginUserModel.type';
import authHeader from './auth-header';
import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';

const API_URL = process.env.REACT_APP_API_URL + '/auth';

const routes = {
  signin: `${API_URL}/signin`,
  signup: `${API_URL}/signup`,
  signout: `${API_URL}/signout`,
};

class AuthService {
  async login({ userIdentifier, password }: LoginUserModel) {
    const response = await axios.post(routes.signin, {
      userIdentifier,
      password,
    });
    // @ts-ignore
    if (response.code === 'ERR_NETWORK') {
      throw new Error('Network error');
    } else if (
      // @ts-ignore
      response.code === 'ERR_BAD_REQUEST' ||
      // @ts-ignore
      response.code === 'ERR_BAD_RESPONSE'
    ) {
      const errorMessage =
        // @ts-ignore
        response?.response?.data?.message ||
        'An error occurred while registering. Please try again later.';
      throw new Error(errorMessage);
    }

    const user = response.data;

    if (user.accessToken && user.refreshToken) {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    }
    // @ts-ignore
    return response.data;
  }

  logout() {
    // Fire-and-forget audit ping to /api/auth/signout BEFORE clearing the
    // token from local storage — the authenticatedAxiosInstance reads the
    // token from there, so the JWT goes with the request. Any failure
    // (network, expired token, server down) is swallowed so it can never
    // block the user from logging out. Server returns 204 No Content.
    try {
      authenticatedAxiosInstance
        .post(routes.signout, null, { headers: authHeader() })
        .catch(() => {});
    } catch {
      /* never block logout */
    }
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.removeItem('user');
      localStorage.removeItem('verified');
    }
  }

  getCurrentUser() {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return {};
    }
    return JSON.parse(localStorage.getItem('user') || '{}');
  }

  async fetchCurrentUser(signal?: AbortSignal) {
    // Get the current user data from localStorage to get the ID and token
    const localUser = this.getCurrentUser();
    if (!localUser || !localUser.id) {
      return null;
    }

    try {
      // Make an API call to get fresh user data
      const response = await authenticatedAxiosInstance.get(
        `${process.env.REACT_APP_API_URL}/users/profile/${localUser.id}`,
        { signal }
      );

      // Merge the fresh user data with the existing tokens
      const updatedUser = {
        ...localUser,
        ...response.data,
        accessToken: localUser.accessToken,
        refreshToken: localUser.refreshToken,
      };

      // Update localStorage with fresh data
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }

      return updatedUser;
    } catch (error) {
      console.error('Error fetching current user:', error);
      return localUser; // Return cached data if API call fails
    }
  }

  async requestVerificationCode() {
    return await authenticatedAxiosInstance.post(
      `${API_URL}/user/send-verification-code`,
      {}
    );
  }

  async verifyCode(code: string) {
    const response = await authenticatedAxiosInstance.post(`${API_URL}/user/verify-code`, {
      code,
    });

    // 🔴 The server now EXCHANGES the token here rather than just saying "ok".
    //
    // A user with MFA enabled signs in holding a token the API refuses on every
    // route except this one (bead cjtr — previously the token was fully
    // privileged and MFA was a React-only guard). Submitting a valid code
    // returns an upgraded pair, and if we drop it on the floor the user stays
    // stuck on a pending token and every subsequent request 403s with
    // MFA_REQUIRED. Persisting it is what completes the login.
    const { accessToken, refreshToken } = response.data ?? {};
    if (accessToken && refreshToken) {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem('user');
        if (stored) {
          localStorage.setItem(
            'user',
            JSON.stringify({ ...JSON.parse(stored), accessToken, refreshToken })
          );
        }
      }
    }

    return response;
  }

  completePasswordReset(token: string, tempPassword: string, newPassword: string) {
    return axios.post(API_URL + '/reset-password/complete', {
      token,
      tempPassword,
      newPassword,
    });
  }

  verifyInvitationToken(token: string) {
    // In the body, not the URL: request telemetry records URLs (lhphcapital-902l).
    return axios.post(`${API_URL}/verify-invitation-token`, { token });
  }

  completeAccountSetup(token: string, password: string) {
    return axios.post(`${API_URL}/complete-account-setup`, { token, password });
  }

  updateUserProfile(token: string, profileData: any) {
    return authenticatedAxiosInstance.post(`${API_URL}/update-profile`, { token, ...profileData });
  }
}

export default new AuthService();
